import React, { useEffect, useMemo, useRef, useState } from "react";

/* ══════════════════════════════════════════════════════════════════════
   FORMIGEN — THE LIVE P&ID CANVAS
   ISA 5.1 compliant piping & instrumentation diagram drafted live by AI.
   Each placement cites the real engineering standard that justified it.
   16s cycle · 4 rotating subsystems · DRAFT · CITE · COMPLY
   Pure SVG + CSS keyframes + React state (no Three.js, no WebGL)
   ══════════════════════════════════════════════════════════════════════ */

const W = 640, H = 560;
const CYCLE = 16000; // ms
const PHASES = [
    { id: "equip",  t0: 0,     t1: 2000  },
    { id: "pipe",   t0: 2000,  t1: 5000  },
    { id: "valve",  t0: 5000,  t1: 8000  },
    { id: "inst",   t0: 8000,  t1: 12000 },
    { id: "loop",   t0: 12000, t1: 14000 },
    { id: "hold",   t0: 14000, t1: 15500 },
    { id: "fade",   t0: 15500, t1: 16000 },
];

/* ══════════════════════════════════════════════════════════════════════
   SUBSYSTEM DEFINITIONS — hand-crafted ISA 5.1 topologies
   Each element is (id, tag, phase-slot, ...geometry, citation)
   ══════════════════════════════════════════════════════════════════════ */

// Helper: build a citation
const cite = (tag, rule, desc, sil = false) => ({ tag, rule, desc, sil });

/* ─── SUBSYSTEM 1: Vertical Separator with Pump-Out ─── */
const SS1 = {
    id: "sep",
    name: "VERTICAL SEPARATOR · FEED-OUT",
    equipment: [
        { id: "V-201",  type: "vessel-v", tag: "V-201",  x: 210, y: 205, w: 44, h: 130,
          cite: cite("V-201", "ASME VIII Div 1", "vertical vessel") },
        { id: "P-101A", type: "pump",     tag: "P-101A", x: 470, y: 380, w: 32, h: 36,
          cite: cite("P-101A", "API 610", "centrifugal pump") },
    ],
    piping: [
        // Feed line: enters left → vessel top-left nozzle
        { id: "L1", path: "M 40 190 H 210", tag: "6\"-P-1045", cite: cite("6\"-P-1045", "B31.3 § 304", "Sch 40 A106") },
        // Vapor outlet: vessel top → exits up
        { id: "L2", path: "M 232 205 V 110", tag: "4\"-V-1012", cite: cite("4\"-V-1012", "B31.3 Cat D", "vapor line") },
        // Liquid outlet: vessel bottom → down → right → pump suction
        { id: "L3", path: "M 232 335 V 395 H 470", tag: "4\"-P-1046", cite: cite("4\"-P-1046", "B31.3 § 304", "pump suction") },
        // Pump discharge: right of pump → exits right
        { id: "L4", path: "M 502 395 H 604", tag: "4\"-P-1047", cite: cite("API 610", "§ 6.4", "discharge spec") },
        // Drain line: vessel bottom-drain offshoot → exits bottom
        { id: "L5", path: "M 210 335 L 180 335 L 180 510", tag: "2\"-D-1008", cite: cite("Drain", "OSHA 1910", "LOTO capable") },
    ],
    valves: [
        { id: "V1", type: "gate",    x: 110, y: 190, cite: cite("V-1", "PIP PIC001", "equipment isolation") },
        { id: "V2", type: "gate",    x: 305, y: 395, cite: cite("V-2", "PIP PIC001", "pump suction isolation") },
        { id: "V3", type: "check",   x: 525, y: 395, cite: cite("CV-1", "API 610", "pump discharge") },
        { id: "V4", type: "control", x: 232, y: 140, cite: cite("PV-201", "ISA 75.01", "vapor pressure control") },
        { id: "V5", type: "psv",     x: 290, y: 165, cite: cite("PSV-201", "ASME VIII § UG-125", "overpressure protection", true) },
        { id: "V6", type: "gate",    x: 180, y: 475, cite: cite("Drain V", "OSHA 1910", "LOTO capable") },
    ],
    instruments: [
        { id: "PT-201",  tag: "PT-201",  x: 296, y: 215, mp: { x: 254, y: 215 },
          cite: cite("PT-201", "ISA 5.1", "pressure transmitter") },
        { id: "LG-201",  tag: "LG-201",  x: 170, y: 270, mp: { x: 210, y: 270 },
          cite: cite("LG-201", "API 541", "external level gauge") },
        { id: "LSH-201", tag: "LSH-201", x: 296, y: 255, mp: { x: 254, y: 255 }, sil: true,
          cite: cite("LSH-201", "IEC 61511", "SIL-2 level switch", true) },
        { id: "TT-201",  tag: "TT-201",  x: 170, y: 310, mp: { x: 210, y: 310 },
          cite: cite("TT-201", "ISA 5.1", "temperature transmitter") },
        { id: "PG-101",  tag: "PG-101",  x: 530, y: 365, mp: { x: 515, y: 395 },
          cite: cite("PG-101", "ASME B40", "pressure gauge") },
        { id: "FE-101",  tag: "FE-101",  x: 560, y: 368, mp: { x: 560, y: 395 },
          cite: cite("FE-101", "API RP 551", "orifice plate") },
        { id: "FT-101",  tag: "FT-101",  x: 590, y: 368, mp: { x: 590, y: 395 },
          cite: cite("FT-101", "API RP 551", "flow transmitter") },
        { id: "PIC-201", tag: "PIC-201", x: 340, y: 140, mp: { x: 296, y: 215 }, panel: true,
          cite: cite("PIC-201", "ISA-88", "pressure controller") },
    ],
    signals: [
        // Pressure control loop: PT-201 → PIC-201 → PV-201 (control valve)
        { id: "S1", path: "M 296 215 L 340 215 L 340 140", cite: cite("PIC loop", "ISA 5.1", "pressure loop 201") },
        { id: "S2", path: "M 340 140 L 252 140", cite: cite("PV-201", "ISA 75.01", "loop 201 final") },
        // Level interlock: LSH-201 → drain valve (SOV)
        { id: "S3", path: "M 296 255 L 360 255 L 360 475 L 200 475", cite: cite("SIF-201", "IEC 61511", "SIL-2 interlock", true) },
    ],
};

/* ─── SUBSYSTEM 2: Heat Exchanger with Temperature Control ─── */
const SS2 = {
    id: "hx",
    name: "HEAT EXCHANGER · TEMP CONTROL",
    equipment: [
        { id: "E-301", type: "hx",       tag: "E-301", x: 235, y: 240, w: 180, h: 44,
          cite: cite("E-301", "TEMA BEM", "shell & tube exchanger") },
        { id: "V-301", type: "drum-h",   tag: "V-301", x: 280, y: 380, w: 90, h: 30,
          cite: cite("V-301", "ASME VIII", "condensate pot") },
    ],
    piping: [
        { id: "L1", path: "M 40 200 L 325 200 L 325 240", tag: "Hot in", cite: cite("8\"-P-2011", "B31.3 § 304", "hot inlet") },
        { id: "L2", path: "M 325 284 L 325 320 L 550 320 L 550 200 L 604 200", tag: "Hot out", cite: cite("8\"-P-2012", "B31.3 § 304", "hot outlet") },
        { id: "L3", path: "M 40 330 L 240 330 L 240 284", tag: "Cold in", cite: cite("6\"-S-3001", "B31.1", "steam supply") },
        { id: "L4", path: "M 240 240 L 240 180 L 604 180", tag: "Cold out", cite: cite("6\"-W-3002", "B31.3", "cold outlet") },
        { id: "L5", path: "M 325 394 L 325 510", tag: "Cond", cite: cite("Steam trap", "ASME B31.1", "condensate drain") },
    ],
    valves: [
        { id: "V1", type: "gate",    x: 140, y: 200, cite: cite("V-1", "PIP PIC001", "block valve") },
        { id: "V2", type: "gate",    x: 480, y: 320, cite: cite("V-2", "PIP PIC001", "block valve") },
        { id: "V3", type: "control", x: 580, y: 180, cite: cite("TV-301", "ISA 75.01", "temperature control valve") },
        { id: "V4", type: "gate",    x: 140, y: 330, cite: cite("V-4", "PIP PIC001", "block valve") },
        { id: "V5", type: "psv",     x: 400, y: 215, cite: cite("PSV-301", "ASME VIII § UG-125", "thermal relief", true) },
        { id: "V6", type: "check",   x: 325, y: 430, cite: cite("Steam trap", "B31.1", "condensate") },
    ],
    instruments: [
        { id: "TT-301",  tag: "TT-301",  x: 580, y: 140, mp: { x: 580, y: 180 }, sil: false,
          cite: cite("TT-301", "ISA 5.1", "temperature transmitter") },
        { id: "TIC-301", tag: "TIC-301", x: 580, y: 100, mp: { x: 580, y: 140 }, panel: true,
          cite: cite("TIC-301", "ISA-84", "Type 1 control") },
        { id: "PT-301",  tag: "PT-301",  x: 180, y: 260, mp: { x: 235, y: 260 },
          cite: cite("PT-301", "ISA 5.1", "shell pressure") },
        { id: "PDT-301", tag: "PDT-301", x: 180, y: 300, mp: { x: 235, y: 280 },
          cite: cite("PDT-301", "ISA 5.1", "differential pressure") },
        { id: "LT-301",  tag: "LT-301",  x: 400, y: 395, mp: { x: 370, y: 395 },
          cite: cite("LT-301", "ISA 5.1", "level transmitter") },
    ],
    signals: [
        { id: "S1", path: "M 580 140 L 580 100", cite: cite("Loop 301", "ISA-84", "cascade control") },
        { id: "S2", path: "M 580 100 L 610 100 L 610 180", cite: cite("TV-301", "ISA 75.01", "loop output") },
    ],
};

/* ─── SUBSYSTEM 3: Flow-Controlled Feed to Tank ─── */
const SS3 = {
    id: "flow",
    name: "FLOW CONTROL · FEED TO TANK",
    equipment: [
        { id: "P-401A", type: "pump", tag: "P-401A", x: 150, y: 250, w: 32, h: 36,
          cite: cite("P-401A", "API 610", "centrifugal pump") },
        { id: "P-401B", type: "pump", tag: "P-401B", x: 150, y: 360, w: 32, h: 36,
          cite: cite("P-401B", "API 610", "redundant N+1") },
        { id: "T-401",  type: "tank", tag: "T-401",  x: 500, y: 240, w: 70, h: 80,
          cite: cite("T-401", "API 650", "atmospheric tank") },
    ],
    piping: [
        { id: "L1", path: "M 40 268 L 150 268",                      tag: "Suct A", cite: cite("8\"-P-4001", "B31.3", "suction header") },
        { id: "L2", path: "M 40 378 L 150 378",                      tag: "Suct B", cite: cite("8\"-P-4001", "B31.3", "suction header") },
        { id: "L3", path: "M 182 268 L 260 268 L 260 320 L 380 320", tag: "Disc A", cite: cite("6\"-P-4002", "B31.3 § 304", "pump discharge") },
        { id: "L4", path: "M 182 378 L 260 378 L 260 320",            tag: "Disc B", cite: cite("6\"-P-4002", "B31.3 § 304", "pump discharge") },
        { id: "L5", path: "M 380 320 L 500 320 L 500 270",             tag: "To T-401", cite: cite("6\"-P-4003", "B31.3", "to tank") },
        { id: "L6", path: "M 535 240 L 535 100",                        tag: "Vent",    cite: cite("Overflow", "API 650", "atmospheric vent") },
        { id: "L7", path: "M 535 320 L 535 510",                        tag: "Drain",   cite: cite("T-401 drain", "OSHA 1910", "to sump") },
    ],
    valves: [
        { id: "V1", type: "gate",    x: 100, y: 268, cite: cite("V-1", "PIP PIC001", "suction isol") },
        { id: "V2", type: "gate",    x: 100, y: 378, cite: cite("V-2", "PIP PIC001", "suction isol") },
        { id: "V3", type: "check",   x: 215, y: 268, cite: cite("CV-1", "API 610", "pump A discharge") },
        { id: "V4", type: "check",   x: 215, y: 378, cite: cite("CV-2", "API 610", "pump B discharge") },
        { id: "V5", type: "control", x: 430, y: 320, cite: cite("FV-401", "ISA 75.01", "flow control") },
    ],
    instruments: [
        { id: "FE-401",  tag: "FE-401",  x: 350, y: 345, mp: { x: 350, y: 320 },
          cite: cite("FE-401", "API RP 551", "orifice plate") },
        { id: "FT-401",  tag: "FT-401",  x: 380, y: 365, mp: { x: 380, y: 320 },
          cite: cite("FT-401", "API RP 551", "flow transmitter") },
        { id: "FIC-401", tag: "FIC-401", x: 430, y: 265, mp: { x: 430, y: 320 }, panel: true,
          cite: cite("FIC-401", "ISA 5.1", "flow controller") },
        { id: "LT-401",  tag: "LT-401",  x: 585, y: 270, mp: { x: 570, y: 270 },
          cite: cite("LT-401", "ISA 5.1", "tank level") },
        { id: "LAH-401", tag: "LAH-401", x: 585, y: 230, mp: { x: 570, y: 250 }, panel: true, sil: false,
          cite: cite("LAH-401", "ISA 5.1", "high level alarm") },
    ],
    signals: [
        { id: "S1", path: "M 380 365 L 430 365 L 430 285", cite: cite("Loop 401", "ISA 5.1", "flow control") },
        { id: "S2", path: "M 430 245 L 430 210 L 430 320", cite: cite("FV-401", "ISA 75.01", "loop output") },
        { id: "S3", path: "M 585 270 L 585 250", cite: cite("LAH-401", "ISA 5.1", "alarm link") },
    ],
};

/* ─── SUBSYSTEM 4: Compressor with Anti-Surge Control ─── */
const SS4 = {
    id: "comp",
    name: "COMPRESSOR · ANTI-SURGE CONTROL",
    equipment: [
        { id: "V-501", type: "vessel-v",  tag: "V-501", x: 130, y: 220, w: 40, h: 110,
          cite: cite("V-501", "ASME VIII Div 1", "KO drum") },
        { id: "K-501", type: "compressor", tag: "K-501", x: 330, y: 260, w: 64, h: 42,
          cite: cite("K-501", "API 617", "centrifugal compressor") },
    ],
    piping: [
        { id: "L1", path: "M 40 240 L 130 240",                       tag: "Inlet",  cite: cite("12\"-G-5001", "B31.3", "inlet") },
        { id: "L2", path: "M 170 270 L 330 270",                       tag: "Suction", cite: cite("10\"-G-5002", "B31.3", "suction") },
        { id: "L3", path: "M 394 270 L 580 270 L 580 420 L 604 420",   tag: "Disch",  cite: cite("8\"-G-5003", "B31.3", "discharge") },
        { id: "L4", path: "M 480 270 L 480 180 L 150 180 L 150 220",   tag: "Recycle",cite: cite("Recycle", "API 617", "anti-surge") },
        { id: "L5", path: "M 150 330 L 150 510",                        tag: "Drain",  cite: cite("2\"-D-5001", "OSHA 1910", "KO drum drain") },
    ],
    valves: [
        { id: "V1", type: "gate",    x: 90,  y: 240, cite: cite("V-1", "PIP PIC001", "inlet isolation") },
        { id: "V2", type: "gate",    x: 250, y: 270, cite: cite("V-2", "PIP PIC001", "suction isolation") },
        { id: "V3", type: "check",   x: 540, y: 270, cite: cite("CV-1", "API 617", "discharge check") },
        { id: "V4", type: "control", x: 315, y: 180, cite: cite("ASV-501", "API 617", "anti-surge valve") },
        { id: "V5", type: "psv",     x: 170, y: 195, cite: cite("PSV-501", "ASME VIII § UG-125", "V-501 relief", true) },
    ],
    instruments: [
        { id: "PT-501A", tag: "PT-501A", x: 300, y: 300, mp: { x: 300, y: 270 },
          cite: cite("PT-501A", "ISA 5.1", "suction pressure") },
        { id: "PT-501B", tag: "PT-501B", x: 430, y: 300, mp: { x: 430, y: 270 },
          cite: cite("PT-501B", "ISA 5.1", "discharge pressure") },
        { id: "FT-501",  tag: "FT-501",  x: 510, y: 300, mp: { x: 510, y: 270 },
          cite: cite("FT-501", "API RP 551", "flow transmitter") },
        { id: "ASC-501", tag: "ASC-501", x: 405, y: 380, mp: { x: 405, y: 330 }, panel: true,
          cite: cite("ASC-501", "API 617", "anti-surge controller") },
        { id: "LG-501",  tag: "LG-501",  x: 90, y: 275, mp: { x: 130, y: 275 },
          cite: cite("LG-501", "API 541", "external level gauge") },
    ],
    signals: [
        { id: "S1", path: "M 300 320 L 300 380 L 385 380", cite: cite("ASC-501", "API 617", "loop input") },
        { id: "S2", path: "M 430 320 L 430 380 L 405 380", cite: cite("ASC-501", "API 617", "loop input") },
        { id: "S3", path: "M 510 320 L 510 380 L 425 380", cite: cite("ASC-501", "API 617", "loop input") },
        { id: "S4", path: "M 405 360 L 405 200 L 335 200", cite: cite("ASV-501", "API 617", "loop output") },
    ],
};

const SUBSYSTEMS = [SS1, SS2, SS3, SS4];

/* ══════════════════════════════════════════════════════════════════════
   ISA 5.1 SYMBOL LIBRARY
   ══════════════════════════════════════════════════════════════════════ */

/* ─── Equipment ─── */
function Vessel({ x, y, w, h, tag, visible, delay = 0 }) {
    const rx = w / 2;
    const cap = h * 0.08;
    const mid = h - 2 * cap;
    return (
        <g className={`rp-eq ${visible ? "rp-eq-in" : "rp-eq-off"}`} style={{ animationDelay: `${delay}ms` }}>
            <path d={`M ${x} ${y + cap} L ${x} ${y + cap + mid}`} className="rp-eq-body" />
            <path d={`M ${x + w} ${y + cap} L ${x + w} ${y + cap + mid}`} className="rp-eq-body" />
            {/* top dished head */}
            <ellipse cx={x + rx} cy={y + cap} rx={rx} ry={cap} className="rp-eq-body" />
            {/* bottom dished head */}
            <ellipse cx={x + rx} cy={y + h - cap} rx={rx} ry={cap} className="rp-eq-body" />
            <text x={x + w + 6} y={y + 12} className="rp-eq-tag">{tag}</text>
        </g>
    );
}

function Pump({ x, y, w, h, tag, visible, delay = 0 }) {
    const cx = x + w / 2, cy = y + h / 2 - 3;
    const r = w / 2;
    return (
        <g className={`rp-eq ${visible ? "rp-eq-in" : "rp-eq-off"}`} style={{ animationDelay: `${delay}ms` }}>
            <circle cx={cx} cy={cy} r={r} className="rp-eq-body" />
            {/* triangle rotor */}
            <path d={`M ${cx - r * 0.55} ${cy + r * 0.35} L ${cx + r * 0.55} ${cy + r * 0.35} L ${cx} ${cy - r * 0.55} Z`}
                  className="rp-eq-body" />
            {/* mounting baseline */}
            <line x1={x - 3} y1={y + h} x2={x + w + 3} y2={y + h} className="rp-eq-body" />
            <text x={cx} y={y + h + 11} textAnchor="middle" className="rp-eq-tag">{tag}</text>
        </g>
    );
}

function HX({ x, y, w, h, tag, visible, delay = 0 }) {
    // shell & tube: rectangle with caps and tube lines
    const capR = h / 2;
    return (
        <g className={`rp-eq ${visible ? "rp-eq-in" : "rp-eq-off"}`} style={{ animationDelay: `${delay}ms` }}>
            <rect x={x + capR} y={y} width={w - 2 * capR} height={h} className="rp-eq-body" />
            <ellipse cx={x + capR} cy={y + h / 2} rx={capR} ry={h / 2} className="rp-eq-body" />
            <ellipse cx={x + w - capR} cy={y + h / 2} rx={capR} ry={h / 2} className="rp-eq-body" />
            {/* tube lines */}
            <line x1={x + capR} y1={y + h * 0.33} x2={x + w - capR} y2={y + h * 0.33} className="rp-eq-tube" />
            <line x1={x + capR} y1={y + h * 0.66} x2={x + w - capR} y2={y + h * 0.66} className="rp-eq-tube" />
            <text x={x + w / 2} y={y - 6} textAnchor="middle" className="rp-eq-tag">{tag}</text>
        </g>
    );
}

function DrumH({ x, y, w, h, tag, visible, delay = 0 }) {
    const capR = h / 2;
    return (
        <g className={`rp-eq ${visible ? "rp-eq-in" : "rp-eq-off"}`} style={{ animationDelay: `${delay}ms` }}>
            <rect x={x + capR} y={y} width={w - 2 * capR} height={h} className="rp-eq-body" />
            <ellipse cx={x + capR} cy={y + h / 2} rx={capR} ry={h / 2} className="rp-eq-body" />
            <ellipse cx={x + w - capR} cy={y + h / 2} rx={capR} ry={h / 2} className="rp-eq-body" />
            <text x={x + w + 6} y={y + h / 2 + 3} className="rp-eq-tag">{tag}</text>
        </g>
    );
}

function Tank({ x, y, w, h, tag, visible, delay = 0 }) {
    return (
        <g className={`rp-eq ${visible ? "rp-eq-in" : "rp-eq-off"}`} style={{ animationDelay: `${delay}ms` }}>
            <rect x={x} y={y} width={w} height={h} className="rp-eq-body" />
            {/* flat top line accent */}
            <line x1={x - 4} y1={y} x2={x + w + 4} y2={y} className="rp-eq-body" />
            <text x={x + w / 2} y={y - 6} textAnchor="middle" className="rp-eq-tag">{tag}</text>
        </g>
    );
}

function Compressor({ x, y, w, h, tag, visible, delay = 0 }) {
    const cx = x + w / 2, cy = y + h / 2;
    return (
        <g className={`rp-eq ${visible ? "rp-eq-in" : "rp-eq-off"}`} style={{ animationDelay: `${delay}ms` }}>
            <ellipse cx={cx} cy={cy} rx={w / 2} ry={h / 2} className="rp-eq-body" />
            {/* rotor triangle */}
            <path d={`M ${cx - 10} ${cy + 6} L ${cx + 10} ${cy + 6} L ${cx} ${cy - 10} Z`} className="rp-eq-body" />
            <text x={cx} y={y - 6} textAnchor="middle" className="rp-eq-tag">{tag}</text>
        </g>
    );
}

function renderEquipment(e, visible, delay) {
    const common = { ...e, visible, delay };
    switch (e.type) {
        case "vessel-v":   return <Vessel {...common} />;
        case "pump":       return <Pump {...common} />;
        case "hx":         return <HX {...common} />;
        case "drum-h":     return <DrumH {...common} />;
        case "tank":       return <Tank {...common} />;
        case "compressor": return <Compressor {...common} />;
        default:           return null;
    }
}

/* ─── Pipe Line (Manhattan routed, stroke-dashoffset draw-in) ─── */
function PipeLine({ d, visible, delay = 0, tag }) {
    // Calculate path length for dash animation; SVG pathLength attr fixes it to 1
    return (
        <g className={`rp-pipe ${visible ? "rp-pipe-in" : "rp-pipe-off"}`} style={{ animationDelay: `${delay}ms` }}>
            <path d={d} className="rp-pipe-path" pathLength="1" />
            {tag && visible && <PipeTag d={d} label={tag} delay={delay + 500} />}
        </g>
    );
}

function PipeTag({ d, label, delay }) {
    // Position tag near midpoint of first segment - approximate via path midpoint
    // Simple heuristic: parse first two coords of path
    const m = d.match(/M\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)[^A-Z]*([HLV])\s*(-?\d+(?:\.\d+)?)(?:\s+(-?\d+(?:\.\d+)?))?/);
    if (!m) return null;
    const x0 = parseFloat(m[1]), y0 = parseFloat(m[2]);
    const cmd = m[3];
    const a = parseFloat(m[4]);
    const b = m[5] ? parseFloat(m[5]) : null;
    let tx, ty, rot = 0;
    if (cmd === "H") { tx = (x0 + a) / 2; ty = y0 - 6; }
    else if (cmd === "V") { tx = x0 + 8; ty = (y0 + a) / 2; rot = 0; }
    else { tx = (x0 + a) / 2; ty = ((b ?? y0) + y0) / 2 - 6; }
    return (
        <text x={tx} y={ty} textAnchor="middle" className="rp-pipe-tag"
              style={{ animationDelay: `${delay}ms` }}>{label}</text>
    );
}

/* ─── Signal Line (dashed) ─── */
function SignalLine({ d, visible, delay = 0 }) {
    return (
        <path d={d} className={`rp-sig ${visible ? "rp-sig-in" : "rp-sig-off"}`} pathLength="1"
              style={{ animationDelay: `${delay}ms` }} />
    );
}

/* ─── Valve symbols ─── */
function Valve({ type, x, y, visible, delay = 0 }) {
    // bowtie base: two triangles meeting at center (x,y)
    const sz = 7;
    const bowtie = `M ${x - sz} ${y - sz} L ${x + sz} ${y + sz} L ${x + sz} ${y - sz} L ${x - sz} ${y + sz} Z`;
    return (
        <g className={`rp-vlv ${visible ? "rp-vlv-in" : "rp-vlv-off"}`} style={{ animationDelay: `${delay}ms` }}>
            <path d={bowtie} className="rp-vlv-body" />
            {type === "check" && (
                <path d={`M ${x + 2} ${y - 3} L ${x + 5} ${y} L ${x + 2} ${y + 3}`} className="rp-vlv-arrow" />
            )}
            {type === "control" && (
                <>
                    <circle cx={x} cy={y - 14} r="5" className="rp-vlv-body" />
                    <line x1={x} y1={y - 9} x2={x} y2={y - sz} className="rp-vlv-body" />
                </>
            )}
            {type === "psv" && (
                <>
                    <line x1={x} y1={y - sz} x2={x + 6} y2={y - 18} className="rp-vlv-body" />
                    <path d={`M ${x + 3} ${y - 14} L ${x + 9} ${y - 14} M ${x + 3} ${y - 16} L ${x + 9} ${y - 16} M ${x + 3} ${y - 18} L ${x + 9} ${y - 18}`}
                          className="rp-vlv-body" />
                </>
            )}
        </g>
    );
}

/* ─── Instrument balloon ─── */
function Balloon({ tag, x, y, mp, panel, sil, visible, delay = 0 }) {
    const r = 11;
    return (
        <g className={`rp-ins ${visible ? "rp-ins-in" : "rp-ins-off"} ${sil ? "rp-ins-sil" : ""}`}
           style={{ animationDelay: `${delay}ms` }}>
            {/* connector line from balloon to measurement point */}
            <line x1={x} y1={y} x2={mp.x} y2={mp.y} className="rp-ins-lead" />
            {/* balloon */}
            <circle cx={x} cy={y} r={r} className="rp-ins-circle" />
            {panel && <line x1={x - r} y1={y} x2={x + r} y2={y} className="rp-ins-circle" />}
            <text x={x} y={y + 3} textAnchor="middle" className="rp-ins-tag">{tag}</text>
        </g>
    );
}

/* ─── Rule Citation Tooltip ─── */
function Citation({ cite, anchor, visible, delay = 0 }) {
    if (!cite) return null;
    // Position tooltip: offset to right/above from anchor
    const tx = anchor.x + 16;
    const ty = anchor.y - 14;
    const w = 168, h = 28;
    // keep within canvas
    const cx = Math.min(Math.max(4, tx), W - w - 4);
    const cy = Math.min(Math.max(28, ty), H - h - 20);
    return (
        <g className={`rp-cite ${visible ? "rp-cite-in" : "rp-cite-off"} ${cite.sil ? "rp-cite-sil" : ""}`}
           style={{ animationDelay: `${delay}ms` }}>
            <rect x={cx} y={cy} width={w} height={h} rx="4" className="rp-cite-bg" />
            {/* orange spark */}
            <path d={`M ${cx + 6} ${cy + 8} L ${cx + 3} ${cy + 13} L ${cx + 6} ${cy + 13} L ${cx + 5} ${cy + 18} L ${cx + 10} ${cy + 11} L ${cx + 7} ${cy + 11} Z`}
                  className="rp-cite-spark" />
            <text x={cx + 18} y={cy + 12} className="rp-cite-tag">{cite.tag}</text>
            <text x={cx + 18} y={cy + 23} className="rp-cite-rule">
                {cite.rule} · <tspan className="rp-cite-desc">{cite.desc}</tspan>
            </text>
        </g>
    );
}

/* ══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════════ */
export default function RachanaPnidCanvas() {
    const [reduceMotion, setReduceMotion] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        setReduceMotion(mq.matches);
        const fn = (e) => setReduceMotion(e.matches);
        mq.addEventListener("change", fn);
        return () => mq.removeEventListener("change", fn);
    }, []);

    const [ssIdx, setSsIdx] = useState(0);
    const [elapsed, setElapsed] = useState(0);
    const startRef = useRef(performance.now());

    /* ── 100ms tick that drives all phase timing ── */
    useEffect(() => {
        if (reduceMotion) return;
        startRef.current = performance.now();
        const tick = () => {
            const e = performance.now() - startRef.current;
            if (e >= CYCLE) {
                startRef.current = performance.now();
                setElapsed(0);
                setSsIdx((i) => (i + 1) % SUBSYSTEMS.length);
            } else {
                setElapsed(e);
            }
        };
        const itv = setInterval(tick, 100);
        return () => clearInterval(itv);
    }, [reduceMotion]);

    const ss = SUBSYSTEMS[ssIdx];
    // In reduced motion → Subsystem 1 frozen in Phase 6 (hold)
    const rEl = reduceMotion ? 14200 : elapsed;
    const phase = PHASES.find((p) => rEl >= p.t0 && rEl < p.t1) || PHASES[PHASES.length - 1];
    const phaseOf = (id) => PHASES.find((p) => p.id === id);

    // Fade everything during fade phase
    const fadeOpacity = phase.id === "fade" ? Math.max(0, 1 - (rEl - 15500) / 500) : 1;

    /* ── Determine which elements are visible based on elapsed time ── */
    const eqVis = (i) => {
        const p = phaseOf("equip");
        const stepDur = (p.t1 - p.t0) / ss.equipment.length; // stagger equipment
        return rEl >= p.t0 + i * stepDur;
    };
    const pipeVis = (i) => {
        const p = phaseOf("pipe");
        const stepDur = (p.t1 - p.t0) / ss.piping.length;
        return rEl >= p.t0 + i * stepDur;
    };
    const vlvVis = (i) => {
        const p = phaseOf("valve");
        const stepDur = (p.t1 - p.t0) / ss.valves.length;
        return rEl >= p.t0 + i * stepDur;
    };
    const insVis = (i) => {
        const p = phaseOf("inst");
        const stepDur = (p.t1 - p.t0) / ss.instruments.length;
        return rEl >= p.t0 + i * stepDur;
    };
    const sigVis = (i) => {
        const p = phaseOf("loop");
        const stepDur = (p.t1 - p.t0) / ss.signals.length;
        return rEl >= p.t0 + i * stepDur;
    };

    /* ── Active citations (max 2 at a time to avoid clutter) ── */
    const activeCitations = useMemo(() => {
        const out = [];
        const CITE_DUR = 1800; // visible for ~1.8s after its trigger
        const pushIfActive = (trigger, anchor, cite) => {
            const t0 = trigger + 200;
            if (rEl >= t0 && rEl < t0 + CITE_DUR) {
                out.push({ anchor, cite, t0 });
            }
        };
        // equipment citations
        ss.equipment.forEach((e, i) => {
            const p = phaseOf("equip");
            const step = (p.t1 - p.t0) / ss.equipment.length;
            pushIfActive(p.t0 + i * step, { x: e.x + e.w, y: e.y + (e.h || 0) / 2 }, e.cite);
        });
        // pipe citations (reduce density: only show ~60%)
        ss.piping.forEach((pp, i) => {
            if (i % 2 !== 0) return;
            const p = phaseOf("pipe");
            const step = (p.t1 - p.t0) / ss.piping.length;
            // anchor: use midpoint via regex
            const m = pp.path.match(/M\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)/);
            const x0 = m ? parseFloat(m[1]) : 100;
            const y0 = m ? parseFloat(m[2]) : 100;
            pushIfActive(p.t0 + i * step, { x: x0 + 40, y: y0 }, pp.cite);
        });
        // valve citations (only the "interesting" ones)
        ss.valves.forEach((v, i) => {
            if (!["psv", "control", "check"].includes(v.type) && i % 3 !== 0) return;
            const p = phaseOf("valve");
            const step = (p.t1 - p.t0) / ss.valves.length;
            pushIfActive(p.t0 + i * step, { x: v.x, y: v.y }, v.cite);
        });
        // instrument citations (SIL + ~40% others)
        ss.instruments.forEach((ins, i) => {
            if (!ins.sil && i % 3 !== 0) return;
            const p = phaseOf("inst");
            const step = (p.t1 - p.t0) / ss.instruments.length;
            pushIfActive(p.t0 + i * step, { x: ins.x, y: ins.y }, ins.cite);
        });
        // signal loop citations (first one only)
        ss.signals.slice(0, 1).forEach((s, i) => {
            const p = phaseOf("loop");
            const step = (p.t1 - p.t0) / ss.signals.length;
            const m = s.path.match(/M\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)/);
            pushIfActive(p.t0 + i * step, { x: m ? parseFloat(m[1]) : 300, y: m ? parseFloat(m[2]) : 300 }, s.cite);
        });
        return out.slice(-2); // at most 2 visible at any time
    }, [ss, rEl]);

    const showComplete = phase.id === "hold" || phase.id === "fade";

    return (
        <div className="rp-wrap" data-testid="rp-canvas" role="img"
             aria-label="Animated illustration: Formigen drafts an ISA 5.1 compliant Piping and Instrumentation Diagram live. Equipment, piping, valves, and instruments are placed progressively. Each placement is annotated with the engineering code — ASME, API, ISA, IEC, B31.3, TEMA — that justifies it. Four process subsystems rotate: a vertical separator with pump-out, a heat exchanger with temperature control, flow-controlled feed to a tank, and a compressor with anti-surge control.">
            <svg viewBox={`0 0 ${W} ${H}`} className={`rp-svg ${reduceMotion ? "rp-reduced" : ""}`}
                 preserveAspectRatio="xMidYMid meet" aria-hidden="true"
                 style={{ opacity: fadeOpacity }}>
                <defs>
                    <pattern id="rp-grid" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
                        <path d="M 24 0 L 0 0 0 24" fill="none" className="rp-grid-minor" />
                    </pattern>
                    <pattern id="rp-grid-major" x="0" y="0" width="96" height="96" patternUnits="userSpaceOnUse">
                        <path d="M 96 0 L 0 0 0 96" fill="none" className="rp-grid-major" />
                    </pattern>
                </defs>

                {/* ── LAYER 0: GRID ── */}
                <rect x="0" y="26" width={W} height={H - 60} fill="url(#rp-grid)" />
                <rect x="0" y="26" width={W} height={H - 60} fill="url(#rp-grid-major)" />

                {/* ── LAYER 1: TOP OVERLAY ── */}
                <rect x="0" y="0" width={W} height="24" className="rp-topbar" />
                <text x="8" y="16" className="rp-top-txt">{ss.name}</text>
                <text x={W / 2} y="16" textAnchor="middle" className="rp-top-txt">LIVE · ISA 5.1</text>
                <g transform={`translate(${W - 8}, 12)`}>
                    <text x="0" y="4" textAnchor="end" className="rp-top-txt">4,173 RULES EMBEDDED</text>
                    <path d="M-120,-4 L-124,1 L-120,1 L-121,6 L-116,-1 L-119,-1 Z" className="rp-spark rp-spark-static" />
                </g>
                <line x1="0" y1="25" x2={W} y2="25" className="rp-hair" />

                {/* ── LAYER 2: PIPING ── */}
                <g className="rp-layer-pipes">
                    {ss.piping.map((p, i) => (
                        <PipeLine key={p.id} d={p.path} tag={p.tag} visible={pipeVis(i)} delay={i * 80} />
                    ))}
                </g>

                {/* ── LAYER 3: EQUIPMENT ── */}
                <g className="rp-layer-eq">
                    {ss.equipment.map((e, i) => (
                        <React.Fragment key={e.id}>{renderEquipment(e, eqVis(i), i * 300)}</React.Fragment>
                    ))}
                </g>

                {/* ── LAYER 4: VALVES ── */}
                <g className="rp-layer-vlv">
                    {ss.valves.map((v, i) => (
                        <Valve key={v.id} {...v} visible={vlvVis(i)} delay={i * 120} />
                    ))}
                </g>

                {/* ── LAYER 5: INSTRUMENTS ── */}
                <g className="rp-layer-ins">
                    {ss.instruments.map((ins, i) => (
                        <Balloon key={ins.id} {...ins} visible={insVis(i)} delay={i * 140} />
                    ))}
                </g>

                {/* ── LAYER 6: SIGNAL LINES / CONTROL LOOPS ── */}
                <g className="rp-layer-sig">
                    {ss.signals.map((s, i) => (
                        <SignalLine key={s.id} d={s.path} visible={sigVis(i)} delay={i * 200} />
                    ))}
                    {/* ambient packets on signal lines once loops drawn */}
                    {phaseOf("loop").t1 <= rEl && ss.signals.map((s, i) => (
                        <circle key={`pkt-${s.id}`} r="1.6" className="rp-sig-pkt">
                            <animateMotion dur="1.8s" repeatCount="indefinite" path={s.path} begin={`${i * 0.4}s`} />
                        </circle>
                    ))}
                </g>

                {/* ── LAYER 7: CITATIONS ── */}
                <g className="rp-layer-cite">
                    {activeCitations.map((c, i) => (
                        <Citation key={`c-${c.t0}-${i}`} cite={c.cite} anchor={c.anchor} visible={true} />
                    ))}
                </g>

                {/* ── LAYER 8: COMPLETION STATUS ── */}
                {showComplete && (
                    <g className="rp-complete" transform="translate(480, 42)">
                        <rect x="-4" y="-10" width="148" height="20" rx="3" className="rp-complete-bg" />
                        <path d="M2,0 L7,5 L15,-4" className="rp-complete-check" />
                        <text x="22" y="4" className="rp-complete-txt">SUBSYSTEM COMPLETE</text>
                    </g>
                )}
                {showComplete && (
                    <text x={W / 2} y={H - 32} textAnchor="middle" className="rp-complete-meta">
                        {ss.equipment.length + ss.valves.length + ss.instruments.length} elements · {3 + ss.valves.length * 2 + ss.instruments.length} rules applied · ISA 5.1 compliant
                    </text>
                )}

                {/* ── DRAFTING INDICATOR ── */}
                {!showComplete && (
                    <g className="rp-drafting" transform={`translate(8, ${H - 28})`}>
                        <circle r="2.5" className="rp-draft-dot" />
                        <path d="M6,-3 L3,1 L6,1 L5,5 L10,-1 L7,-1 Z" className="rp-spark" transform="translate(16, 0)" />
                        <text x="28" y="3" className="rp-draft-label">drafting</text>
                    </g>
                )}

                {/* ── BOTTOM OVERLAY ── */}
                <line x1="0" y1={H - 18} x2={W} y2={H - 18} className="rp-hair" />
                <text x="8"   y={H - 4} className="rp-bot-label">DRAFT</text>
                <text x={W / 2}   y={H - 4} textAnchor="middle" className="rp-bot-label rp-bot-accent">CITE</text>
                <text x={W - 8}   y={H - 4} textAnchor="end" className="rp-bot-label rp-bot-accent">COMPLY</text>
            </svg>

            <span className="sr-only">
                Formigen drafts an ISA 5.1 compliant P&amp;ID live. Equipment, piping, valves, and instruments are placed progressively, each accompanied by the engineering rule that justifies it — ASME VIII, API 610, ISA 5.1, IEC 61511, B31.3, TEMA, PIP PIC001. Four subsystems rotate continuously.
            </span>
        </div>
    );
}
